<script>
    import Button from './Button.svelte';

    import FieldCheckbox from './FieldCheckbox.svelte';
    import {
        cookie_consent_asked,
        cookie_consent_marketing,
        cookie_consent_preferences,
        isIOS,
        isMac
    } from './stores/globals';
    import { onMount } from 'svelte';
    import { slide } from 'svelte/transition';
    import { cubicOut } from 'svelte/easing';
    import Prose from './Prose.svelte';
    import { get } from 'svelte/store';

    export let text_html = '';

    let mode = null;

    let show_modal = false;

    const show = () => (show_modal = true);

    onMount(() => {
        cookie_prefs = get(cookie_consent_preferences);
        cookie_marketing = get(cookie_consent_marketing);

        let initial_show_timer = null;
        let cancelled = false;

        const reveal_if_needed = () => {
            if (cancelled || $cookie_consent_asked) return;
            initial_show_timer = setTimeout(() => {
                show_modal = true;
            }, 1000);
        };

        fetch('/api/region')
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
            .then((data) => {
                if (cancelled) return;
                mode = data?.cookie_consent_mode === 'opt-out' ? 'opt-out' : 'opt-in';

                if (mode === 'opt-out' && !$cookie_consent_asked) {
                    // Outside consent-required jurisdictions: treat the banner as a notice,
                    // not a gate. Grant by default so pixels load immediately; the user can
                    // still opt out via Manage. Persistence happens once they dismiss.
                    $cookie_consent_preferences = true;
                    $cookie_consent_marketing = true;
                }

                reveal_if_needed();
            });

        document.addEventListener('show_consent_notice', show);

        return () => {
            cancelled = true;
            if (initial_show_timer) clearTimeout(initial_show_timer);
            document.removeEventListener('show_consent_notice', show);
        };
    });

    let cookie_prefs = false;
    let cookie_marketing = false;

    let customize = false;

    const handle_allow_all = () => {
        $cookie_consent_preferences = true;
        $cookie_consent_asked = true;
        $cookie_consent_marketing = true;
        show_modal = false;
    };
    const handle_allow_selected = () => {
        $cookie_consent_preferences = cookie_prefs;
        $cookie_consent_asked = true;
        $cookie_consent_marketing = cookie_marketing;
        show_modal = false;
    };
    const handle_deny = () => {
        $cookie_consent_preferences = false;
        $cookie_consent_asked = true;
        $cookie_consent_marketing = false;
        show_modal = false;
    };
    const handle_customize = () => {
        customize = true;
    };

    const handle_dismiss_notice = () => {
        // Opt-out mode "Got it": persist whatever defaults are currently set.
        $cookie_consent_asked = true;
        show_modal = false;
    };

    const handle_close = () => {
        show_modal = false;
    };
</script>

{#if show_modal}
    {#if mode === 'opt-out' && !customize}
        <div
            class="
            fixed z-40 bottom-0 right-0
            bg-surface-invert-main rounded-t-2xl text-symbols-invert-default
            flex flex-col gap-3 p-4 pb-safe
            w-fit max-w-full
            md:right-4 md:bottom-4 md:rounded-2xl
            md:flex-row md:items-center md:gap-6
        "
            in:slide={{ duration: 300, easing: cubicOut }}
            out:slide={{ duration: 300, easing: cubicOut }}
        >
            <Prose invert xs class="prose-p:m-0 flex-1 md:pb-4">
                We use cookies to improve Critikid and measure usage. <a
                    href="/privacy">Privacy Policy</a
                >.
            </Prose>

            <div
                class="flex flex-row flex-nowrap gap-2 pb-4 {isMac || isIOS
                    ? 'flex-row-reverse'
                    : ''}"
            >
                <Button primary invert sm on:click={handle_dismiss_notice}
                    >Ok</Button
                >
                <Button outline invert sm on:click={handle_customize}
                    >Settings</Button
                >
            </div>
        </div>
    {:else}
        <div
            class="
            fixed z-40 bottom-0 left-0 right-0
            bg-surface-invert-main rounded-t-2xl text-symbols-invert-default
            flex flex-col gap-4 p-4 pb-safe max-w-sm
            md:left-auto md:right-4 md:bottom-4 md:rounded-2xl
        "
            in:slide={{ duration: 300, easing: cubicOut }}
            out:slide={{ duration: 300, easing: cubicOut }}
        >
            <Button
                xxl
                nop
                class="!absolute right-4 top-2"
                on:click={handle_close}>&times;</Button
            >

            <Prose invert xs class="prose-p:m-0 pr-2">
                {@html text_html}

                {#if customize}
                    <div class="flex flex-col gap-4 mt-4">
                        <FieldCheckbox checked disabled invert>
                            <strong>Necessary</strong>
                            <svelte:fragment slot="small"
                                >needed to run the site, keep it secure, and
                                include basic analytics</svelte:fragment
                            >
                        </FieldCheckbox>
                        <FieldCheckbox bind:checked={cookie_prefs} invert>
                            <strong>Preferences</strong>
                            <svelte:fragment slot="small"
                                >remembers your details to ease sign in and
                                speed up checkout</svelte:fragment
                            >
                        </FieldCheckbox>
                        <FieldCheckbox bind:checked={cookie_marketing} invert>
                            <strong>Marketing</strong>
                            <svelte:fragment slot="small"
                                >allows advertising pixels on non-student pages</svelte:fragment
                            >
                        </FieldCheckbox>
                    </div>
                {/if}
            </Prose>

            <div
                class="pb-4 flex flex-row flex-nowrap gap-2 {isMac || isIOS
                    ? 'flex-row-reverse'
                    : ''}"
            >
                <Button primary on:click={handle_allow_all}>Accept All</Button>
                {#if !customize}
                    <Button outline invert on:click={handle_customize}
                        >Customize</Button
                    >
                {:else}
                    <Button outline invert on:click={handle_allow_selected}
                        >Accept Selected</Button
                    >
                {/if}
                <Button outline invert on:click={handle_deny}>Deny</Button>
            </div>
        </div>
    {/if}
{/if}
